import { COUNTRIES, PRIORITY_COUNTRY_CODES, localizedCountryName, type CountryEntry } from "./countries";

/**
 * International calling codes (without the leading "+"), keyed by ISO country code.
 * NANP islands carry their full 1+area-code prefix so a pasted "+1242…" resolves to
 * the Bahamas rather than the US; Vatican City shares Italy's +39.
 */
export const DIAL_CODES: Record<string, string> = {
  AD: "376", AE: "971", AF: "93", AG: "1268", AL: "355", AM: "374", AO: "244", AR: "54",
  AT: "43", AU: "61", AZ: "994", BA: "387", BB: "1246", BD: "880", BE: "32", BF: "226",
  BG: "359", BH: "973", BI: "257", BJ: "229", BN: "673", BO: "591", BR: "55", BS: "1242",
  BT: "975", BW: "267", BY: "375", BZ: "501", CA: "1", CD: "243", CF: "236", CG: "242",
  CH: "41", CI: "225", CL: "56", CM: "237", CN: "86", CO: "57", CR: "506", CU: "53",
  CV: "238", CY: "357", CZ: "420", DE: "49", DJ: "253", DK: "45", DM: "1767", DO: "1809",
  DZ: "213", EC: "593", EE: "372", EG: "20", ER: "291", ES: "34", ET: "251", FI: "358",
  FJ: "679", FM: "691", FR: "33", GA: "241", GB: "44", GD: "1473", GE: "995", GH: "233",
  GM: "220", GN: "224", GQ: "240", GR: "30", GT: "502", GW: "245", GY: "592", HN: "504",
  HR: "385", HT: "509", HU: "36", ID: "62", IE: "353", IL: "972", IN: "91", IQ: "964",
  IR: "98", IS: "354", IT: "39", JM: "1876", JO: "962", JP: "81", KE: "254", KG: "996",
  KH: "855", KI: "686", KM: "269", KN: "1869", KP: "850", KR: "82", KW: "965", KZ: "7",
  LA: "856", LB: "961", LC: "1758", LI: "423", LK: "94", LR: "231", LS: "266", LT: "370",
  LU: "352", LV: "371", LY: "218", MA: "212", MC: "377", MD: "373", ME: "382", MG: "261",
  MH: "692", MK: "389", ML: "223", MM: "95", MN: "976", MR: "222", MT: "356", MU: "230",
  MV: "960", MW: "265", MX: "52", MY: "60", MZ: "258", NA: "264", NE: "227", NG: "234",
  NI: "505", NL: "31", NO: "47", NP: "977", NR: "674", NZ: "64", OM: "968", PA: "507",
  PE: "51", PG: "675", PH: "63", PK: "92", PL: "48", PS: "970", PT: "351", PW: "680",
  PY: "595", QA: "974", RO: "40", RS: "381", RU: "7", RW: "250", SA: "966", SB: "677",
  SC: "248", SD: "249", SE: "46", SG: "65", SI: "386", SK: "421", SL: "232", SM: "378",
  SN: "221", SO: "252", SR: "597", SS: "211", ST: "239", SV: "503", SY: "963", SZ: "268",
  TD: "235", TG: "228", TH: "66", TJ: "992", TL: "670", TM: "993", TN: "216", TO: "676",
  TR: "90", TT: "1868", TV: "688", TZ: "255", UA: "380", UG: "256", US: "1", UY: "598",
  UZ: "998", VA: "39", VC: "1784", VE: "58", VN: "84", VU: "678", WS: "685", YE: "967",
  ZA: "27", ZM: "260", ZW: "263",
};

export type PhoneCountry = CountryEntry & { dialCode: string };

export const DEFAULT_PHONE_COUNTRY_CODE = "HU";

// Where the leading 0 is part of the number itself, not a trunk prefix.
const KEEPS_LEADING_ZERO = new Set(["IT", "SM", "VA"]);

/**
 * Every country with a calling code, in picker order: our home markets first, then
 * the usual foreign-guest countries, then the rest A–Z.
 */
export const PHONE_COUNTRIES: PhoneCountry[] = (() => {
  const withDial = COUNTRIES.flatMap((entry) => {
    const dialCode = DIAL_CODES[entry.code];
    return dialCode ? [{ ...entry, dialCode }] : [];
  });
  const pinned = [...new Set(["HU", "SK", ...PRIORITY_COUNTRY_CODES])];
  const head = pinned.flatMap((code) => withDial.filter((entry) => entry.code === code));
  const rest = withDial
    .filter((entry) => !pinned.includes(entry.code))
    .sort((a, b) => a.name.localeCompare(b.name, "en"));
  return [...head, ...rest];
})();

export function phoneCountryByCode(code: string | null | undefined): PhoneCountry {
  const upper = (code ?? "").toUpperCase();
  return (
    PHONE_COUNTRIES.find((entry) => entry.code === upper) ??
    PHONE_COUNTRIES.find((entry) => entry.code === DEFAULT_PHONE_COUNTRY_CODE)!
  );
}

// Owner of a calling code shared by several countries, when the caller has no preference.
const SHARED_CODE_OWNERS: Record<string, string> = { "1": "US", "7": "RU", "39": "IT" };

/** Longest calling code that prefixes `digits`; ties go to `preferredCode`, then the code's owner. */
function countryForInternationalDigits(digits: string, preferredCode: string): PhoneCountry | undefined {
  const matches = PHONE_COUNTRIES.filter((entry) => digits.startsWith(entry.dialCode));
  const longest = Math.max(0, ...matches.map((entry) => entry.dialCode.length));
  const best = matches.filter((entry) => entry.dialCode.length === longest);
  return (
    best.find((entry) => entry.code === preferredCode.toUpperCase()) ??
    best.find((entry) => entry.code === SHARED_CODE_OWNERS[entry.dialCode]) ??
    best[0]
  );
}

/**
 * Splits a stored phone number ("+36701234567") into its country and the national
 * part ("701234567"). Numbers without a "+"/"00" prefix are treated as national
 * numbers of `fallbackCode`.
 */
export function splitPhoneNumber(
  value: string | null | undefined,
  fallbackCode: string = DEFAULT_PHONE_COUNTRY_CODE,
): { country: PhoneCountry; national: string } {
  const trimmed = (value ?? "").trim();
  const international = trimmed.startsWith("+")
    ? trimmed.slice(1)
    : trimmed.startsWith("00")
      ? trimmed.slice(2)
      : null;
  if (international === null) {
    return { country: phoneCountryByCode(fallbackCode), national: trimmed.replace(/[^\d ]/g, "") };
  }
  const digits = international.replace(/\D/g, "");
  const country = countryForInternationalDigits(digits, fallbackCode);
  return country
    ? { country, national: digits.slice(country.dialCode.length) }
    : { country: phoneCountryByCode(fallbackCode), national: digits };
}

/**
 * Builds the stored "+<dial><national>" value. Drops the domestic trunk prefix people
 * habitually type ("06 70 …" in Hungary, "0905 …" in Slovakia). Empty input stays empty.
 */
export function joinPhoneNumber(country: PhoneCountry, national: string): string {
  const digits = national.replace(/\D/g, "");
  const withoutTrunk =
    country.code === "HU"
      ? digits.replace(/^06/, "").replace(/^0+/, "")
      : KEEPS_LEADING_ZERO.has(country.code)
        ? digits
        : digits.replace(/^0+/, "");
  return withoutTrunk ? `+${country.dialCode}${withoutTrunk}` : "";
}

function normalizeForSearch(text: string) {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

/**
 * Filters the phone picker by country name (any supported language, accents
 * optional), ISO code ("hu"), or calling code ("36", "+36", "0036").
 */
export function searchPhoneCountries(query: string, lang: string): PhoneCountry[] {
  const q = normalizeForSearch(query);
  if (!q) return PHONE_COUNTRIES;

  const dialQuery = /^(\+|00)?\d+$/.test(q) ? q.replace(/^(\+|00)/, "") : null;
  if (dialQuery) {
    const matches = PHONE_COUNTRIES.filter((entry) => entry.dialCode.startsWith(dialQuery));
    // Exact code first (typing "36" puts Hungary above +3xx countries).
    return [
      ...matches.filter((entry) => entry.dialCode === dialQuery),
      ...matches.filter((entry) => entry.dialCode !== dialQuery),
    ];
  }

  const scored = PHONE_COUNTRIES.flatMap((entry) => {
    const names = [localizedCountryName(entry, lang), entry.name, ...Object.values(entry.names)].map(
      normalizeForSearch,
    );
    const rank =
      entry.code.toLowerCase() === q
        ? 0
        : names.some((name) => name.startsWith(q))
          ? 1
          : names.some((name) => name.includes(q))
            ? 2
            : null;
    return rank === null ? [] : [{ entry, rank }];
  });
  return scored.sort((a, b) => a.rank - b.rank).map(({ entry }) => entry);
}

const NATIONAL_NUMBER_EXAMPLES: Record<string, string> = {
  HU: "70 123 4567",
  SK: "905 123 456",
};

/** Placeholder for the national-number part of a phone input, when we know the format. */
export function nationalNumberExample(countryCode: string): string | undefined {
  return NATIONAL_NUMBER_EXAMPLES[countryCode.toUpperCase()];
}
